/* AthletesCacheMixin.ts */

import { Component, Mixins } from 'vue-property-decorator';
import { StringsMixin } from './StringsMixin';
import { ObjectCache } from '@/helpers/object-cache';
import { AthleteProfileModel, BAPurchaseModel, SportModel } from '@/models';
import { athleteApi } from '@/api/AthleteApi';
import { SportName } from '@/../types';
import { sportStore } from '@/store';
import { baPurchaseApi } from '@/api/BAPurchaseApi';
import { RecruitingProfileModel } from '@/models/athlete/RecruitingProfileModel';

class DataStorage {
	id: string;
	data: Record<string, any> = {};
}
class PurchaseCache extends ObjectCache<BAPurchaseModel> {
	find(parentId: string, productId: string): BAPurchaseModel {
		const purchases = Object.values(this.cache);
		return purchases.find(p => p.parentId === parentId && p.productId === productId);
	}
}

@Component
export class AthletesCacheMixin extends Mixins(StringsMixin) {
	athletesCache: ObjectCache<AthleteProfileModel> = new ObjectCache<AthleteProfileModel>();
	recruitingProfileCache: ObjectCache<RecruitingProfileModel> = new ObjectCache<RecruitingProfileModel>();
	athletesDataCache: ObjectCache<DataStorage> = new ObjectCache<DataStorage>();
	sportsCache: ObjectCache<SportModel> = new ObjectCache<SportModel>();
	purchaseCache: PurchaseCache = new PurchaseCache();

	async AthleteRetrieve(athleteId: string): Promise<AthleteProfileModel> {
		if( this.IsEmpty(athleteId) ) return undefined;

		let athlete = this.athletesCache.get(athleteId);
		if( this.IsNotEmpty(athlete) ) return new AthleteProfileModel().load(athlete);

		try {
			athlete = await athleteApi.publicFindById(athleteId);
		} catch(e) {
			console.warn(`Athlete not found: ${athleteId}: ${e}`);
		}
		if( this.IsEmpty(athlete) ) return undefined;

		this.athletesCache.set(athlete);		
		return new AthleteProfileModel().load(athlete);
	}
	async RecruitingProfileRetrieve(athleteId: string): Promise<RecruitingProfileModel> {
		if( this.IsEmpty(athleteId) ) return undefined;
		
		let profile = this.recruitingProfileCache.get(athleteId);
		if( this.IsNotEmpty(profile) ) return profile;

		profile = await athleteApi.getAthleteRecruitingProfile(athleteId, {as : 'coach'});
		if( this.IsEmpty(profile) ) return undefined;

		this.recruitingProfileCache.set(profile);
		return profile;
	}
	async AthleteDataRetrieve(athleteId: string, data: Record<string, any> = {}): Promise<DataStorage> {
		if( this.IsEmpty(athleteId) ) return undefined;

		let athleteData = this.athletesDataCache.get(athleteId);
		if( this.IsNotEmpty(athleteData) ) return athleteData;

		athleteData = await this.fetchAthleteData(athleteId, data);
		if( this.IsEmpty(athleteData) ) return undefined;

		this.athletesDataCache.set(athleteData);		
		return athleteData;
	}
	async SportRetrieve(sportName: SportName): Promise<SportModel> {
		if( this.IsEmpty(sportName) ) return undefined;

		let sportModel = this.sportsCache.get(sportName);
		if( this.IsNotEmpty(sportModel) ) return sportModel;

		sportModel = await sportStore.findSportByName(sportName);
		if( this.IsEmpty(sportModel) ) return undefined;
		
		this.sportsCache.set(sportModel);
		return sportModel;
	}
	async PurchaseRetrieve(purchaseId: string, athleteId: string, eventId: string): Promise<BAPurchaseModel> {
		if( this.IsEmpty(purchaseId) && ( this.IsEmpty(athleteId) || this.IsEmpty(eventId) ) ) return undefined;

		let purchase: BAPurchaseModel = undefined;
		if( this.IsNotEmpty(purchaseId) ) {
			// find purchase in the cache
			purchase = this.purchaseCache.get(purchaseId);
			if( this.IsNotEmpty(purchase) ) return new BAPurchaseModel().load(purchase);;
		} else {
			// find purchase in the cache using athleteId and eventId
			purchase = this.purchaseCache.find(athleteId, eventId);
			if( this.IsNotEmpty(purchase) ) return new BAPurchaseModel().load(purchase);;
		}

		// purchase not found, find using API
		purchase = await baPurchaseApi.findPurchase(athleteId, eventId);
		if( this.IsEmpty(purchase) ) return undefined;

		// purchase found, add to cache
		this.purchaseCache.set(purchase)
		return new BAPurchaseModel().load(purchase);
	}

	async fetchAthleteData(athleteId: string, data: Record<string, any> = {}): Promise<DataStorage> {
		if( !athleteId ) return {id: athleteId, data};

		const purchases: Array<BAPurchaseModel> = await baPurchaseApi.findByAthleteId(athleteId);
		if( this.IsEmpty(purchases) ) return {id: athleteId, data};

		const purchasesData: Record<string, any> = purchases.reduce((accumulator, p) => {
			return {
				...accumulator,
				...(p.data || {})
			}
		}, {});
		data = {...purchasesData, ...data};
		return {id: athleteId, data};
	}
}
